/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { StyledEngineProvider, Theme } from "@mui/material/styles";
import { LightTheme, DarkTheme, LuiBackground, LuiThemeProvider } from '@jda/lui-common-component-library-mui5';
import {
  useDomHistoryMonitor,
  PortalMessageService,
  Breadcrumbs as BreadcrumbsMessage,
  isIFrame,
  ThemeSwitchResponseMessage,
  ThemeSwitchRequestMessage,
  Message,
  MessageActions,
  ApplicationInfoRequestMessage
} from '@jda/lui-portal-utilities';
import { Breadcrumbs } from 'react-breadcrumbs';
import { ModulesContainer } from './container';
import UserDetails from './components/UserDetails';
import AddUser from './components/AddUser';
import AddUserGroup from './container/AddUserGroup';
import AddSubscriptions from './container/AddSubscriptions';
import TopicSubscriptions from './container/TopicSubscriptions';
import MachineDetails from './components/MachineDetails/MachineDetails';

const themes = { dark: DarkTheme, light: LightTheme };
const App: React.FC = () => {
  const [theme, setTheme] = useState<Theme>(LightTheme);
  useDomHistoryMonitor();

  const portalEventsHandler = (message: Message) => {
    if (message.action === MessageActions.ThemeSwitchResponse) {
      const themeResponse = message as ThemeSwitchResponseMessage;
      console.log("Received theme switch response:", themeResponse);
      // Prioritize themeObject if available, otherwise use the legacy theme string
      setTheme(themeResponse.themeObject ?? themes[themeResponse.theme]);
    }
  };

  useEffect(() => {
    if (isIFrame()) {
      const applicationInfoRequestMessage = new ApplicationInfoRequestMessage();
      PortalMessageService.getInstance().sendMessageToPortal(applicationInfoRequestMessage);
    }
    const portalMessageService: any = PortalMessageService.getInstance();
    portalMessageService.registerListener(portalEventsHandler);

    const themeSwitchRequestMessage = new ThemeSwitchRequestMessage();
    portalMessageService.sendMessageToPortal(themeSwitchRequestMessage);

    return () => {
      portalMessageService.removeListener(portalEventsHandler);
    };
  }, []);

  return (
    <div className="app">
      {isIFrame() ? (
        <Breadcrumbs
          className="breadcrumb-helper"
          setCrumbs={(breadcrumbs: any) => {
            const message = new BreadcrumbsMessage(breadcrumbs);
            PortalMessageService.getInstance().sendMessageToPortal(message);
            return breadcrumbs;
          }}
        />
      ) : null}
      <LuiThemeProvider theme={theme} sizeMode={theme.sizeMode ?? 'standard'}>
        <StyledEngineProvider injectFirst={true}>
          <LuiBackground>
            <Route exact path="/"><ModulesContainer /></Route>
            <Route path={'/userDetail'} component={UserDetails} />
            <Route path={'/addUser'} component={AddUser} />
            <Route path={'/userGroup'} component={AddUserGroup} />
            <Route
              path="/machineDetail"
              render={({ match: { url } }) => (
                <>
                  <Route path={`${url}/:machineId`} component={MachineDetails} exact />
                  <Route path={`${url}/addSubscriptions/:machineId`} component={AddSubscriptions} />
                  <Route path={`${url}/topicSubscriptions/:machineId`} component={TopicSubscriptions} />
                </>
              )}
            />
          </LuiBackground>
        </StyledEngineProvider>
      </LuiThemeProvider>
    </div>
  );
};

export default App;
